/**
 * useBoardMultiSelect - Hook for multi-select task management
 * Wraps selectionStore with keyboard listeners and convenience methods
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { selectionStore } from '../stores/selectionStore';
import type { Task } from '../../types';
import { isFocusInEditable } from '../../lib/domUtils';

interface UseMultiSelectOptions {
  tasks: Task[];
  onRemoveSelected?: () => void;
}

export interface UseMultiSelectResult {
  selectedTaskIds: Set<string>;
  isBoxSelecting: boolean;
  selectTask: (taskId: string, additive: boolean) => void;
  selectTasks: (taskIds: string[]) => void;
  addTasks: (taskIds: string[]) => void;
  clearSelection: () => void;
  selectionCount: number;
}

export function useBoardMultiSelect({ tasks, onRemoveSelected }: UseMultiSelectOptions): UseMultiSelectResult {
  const selectedIdsArray = useSelector(selectionStore, s => s.context.selectedIds);
  const isBoxSelecting = useSelector(selectionStore, s => s.context.isBoxSelecting);

  const selectedTaskIds = useMemo(() => new Set(selectedIdsArray), [selectedIdsArray]);

  const selectTask = useCallback((taskId: string, additive: boolean) => {
    if (additive) {
      selectionStore.send({ type: 'selection.toggle', taskId });
    } else {
      selectionStore.send({ type: 'selection.selectOne', taskId });
    }
  }, []);

  const selectTasks = useCallback((taskIds: string[]) => {
    selectionStore.send({ type: 'selection.set', taskIds });
  }, []);

  const addTasks = useCallback((taskIds: string[]) => {
    selectionStore.send({ type: 'selection.add', taskIds });
  }, []);

  const clearSelection = useCallback(() => {
    selectionStore.send({ type: 'selection.clear' });
  }, []);

  // Prune invalid IDs when tasks change
  useEffect(() => {
    const validIds = new Set(tasks.map(t => t.id));
    selectionStore.send({ type: 'selection.prune', validIds });
  }, [tasks]);

  // Keyboard listeners
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isFocusInEditable()) return;

      if (e.key === 'Escape') {
        selectionStore.send({ type: 'selection.clear' });
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const current = selectionStore.getSnapshot().context.selectedIds;
        if (current.length > 0 && onRemoveSelected) {
          e.preventDefault();
          onRemoveSelected();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onRemoveSelected]);

  return {
    selectedTaskIds,
    isBoxSelecting,
    selectTask,
    selectTasks,
    addTasks,
    clearSelection,
    selectionCount: selectedIdsArray.length,
  };
}
